import { useEffect, type ReactNode } from 'react'
import type { EditorMode } from '@/msgdeskRuntime/contracts/editorActions'
import { ContentView, resolveContentDocument } from '@/app/richEdit/content'
import { EmptyContent, MainContent } from '@/deskUiSkeletons/Page/MainContent'
import { legacyContractAttrs, legacyHookAttrs, type LegacyContractName } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import {
  selectAccessGrant,
  selectAccessState,
  selectDeskIdentity,
  selectMainHeadingText,
  selectMainState,
  selectRootFeedIds,
  selectSectionPaging,
  selectSubRenderState,
} from '@/msgdeskRuntime/state'
import { styleFromString } from '@/msgdeskRuntime/legacyBridge/legacyActions'
import { useSubsStore } from '@/state/MsgTreeProvider'
import { AnswersPanel, MenuSubsPanels, MessagesPanel } from '@/deskUiSkeletons/Page/Panels'
import { RootSubsFeed } from '@/deskUiSkeletons/subs/SubsFeed'
import { MoreLink } from '@/deskUiSkeletons/Shared/Shared'
import { EnterPinForm, LoadingOverlay, ShellOverlays } from '@/deskUiSkeletons/Shared/Overlays'
import {
  QuickReplyComposer,
  showQuickReplyModal,
  useQuickReplyTarget,
} from '@/deskUiSkeletons/Shared/QuickReplyModal'
import { PinDialog } from '@/deskUiSkeletons/Shared/PinDialog'
import { NotesMenu } from './NotesMenu'
import { NotesEditorTemplate } from './editor/NotesEditorTemplate'
import {
  NotesCheckedsInfo,
  NotesFoldSubsButton,
  NotesPagingBar,
  NotesRefreshSubs,
} from './panels/NotesControls'
import { NotesMainAttachments } from './NotesMainAttachments'
import { useNotesDialog } from './NotesDialogContext'

type WorkspaceKind = 'main' | 'editor' | 'attachments' | 'favs' | 'trash' | 'drafts'

const workspaceByKind: Record<WorkspaceKind, { contract: LegacyContractName; style: string }> = {
  main: { contract: 'workspace.main', style: '' },
  editor: { contract: 'workspace.editor', style: 'height:100%;' },
  attachments: { contract: 'workspace.attachments', style: '' },
  favs: { contract: 'workspace.favs', style: '' },
  trash: { contract: 'workspace.trash', style: '' },
  drafts: { contract: 'workspace.drafts', style: '' },
}

function WorkspaceSection({ kind, children }: { kind: WorkspaceKind; children?: ReactNode }) {
  const { state } = useSubsStore()
  const info = workspaceByKind[kind]
  const visible = state.chromeByContract[info.contract]?.visible !== false
  return (
    <section
      {...legacyHookAttrs(info.contract, `md-notes-workspace md-notes-workspace--${kind} min-w-0${visible ? '' : ' hidden'}${kind === 'main' || kind === 'editor' ? ' flex-1' : ''}`)}
      style={{ ...styleFromString(info.style), ...(visible ? {} : { display: 'none' }) }}
      {...(visible ? {} : { 'data-md-react-display': 'none' })}
    >
      {children}
    </section>
  )
}

function NotesMainMessage() {
  const { state } = useSubsStore()
  const main = selectMainState(state)

  return (
    <article {...legacyHookAttrs('main.root', 'md-notes-main mx-auto mb-8 max-w-2xl rounded-lg border border-[#e0e0e0] bg-white px-5 py-4 shadow-sm')}>
      <header {...legacyHookAttrs('main.header', 'flex items-start justify-between gap-4')}>
        <div className="min-w-0">
          <div {...legacyHookAttrs('main.avatarFrame', 'hidden')} hidden aria-hidden="true"><img {...legacyContractAttrs('main.avatarImage')} alt="" /></div>
          <span {...legacyHookAttrs('main.nameKeySign', 'hidden')} hidden aria-hidden="true">@</span>
          <span {...legacyHookAttrs('main.authorFrame', 'text-xs font-medium text-[#5f6368]')}>{main.author || main.nameKey || 'Notes'}</span>
          <div {...legacyHookAttrs('main.emailRegion', 'hidden')} hidden aria-hidden="true" />
          <div {...legacyHookAttrs('main.subjectRegion', 'mt-1')}>
            <div {...legacyHookAttrs('main.subjectLine', '')}>
              <h1 {...legacyHookAttrs('main.subjectText', 'break-words text-xl font-medium text-[#202124]')}>{selectMainHeadingText(state) || 'Notes'}</h1>
            </div>
            <div {...legacyHookAttrs('main.lineClear', 'hidden')} hidden aria-hidden="true" />
          </div>
        </div>
        <span {...legacyHookAttrs('main.datetime', 'flex-none text-xs text-[#5f6368]')}>{main.datetime || ''}</span>
      </header>
      <div {...legacyHookAttrs('main.quad', 'mt-3')}>
        <div {...legacyHookAttrs('main.body', 'relative')}>
          <NotesFoldSubsButton kind="msg" label="Fold note" />
          <div {...legacyHookAttrs('main.iframe', 'mt-3 text-sm leading-6 text-[#3c4043] [&_img]:max-w-full')}>
            <MainContent frame={{ kind: 'solid' }} />
          </div>
        </div>
        <div {...legacyHookAttrs('main.editMount', 'hidden')} hidden aria-hidden="true" />
      </div>
    </article>
  )
}

function PinnedGroup() {
  const { state } = useSubsStore()
  if (state.renderProjection.mode !== 'msg') return null
  const answerIds = selectRootFeedIds(state, false, 'favs')
  const messageIds = selectRootFeedIds(state, true, 'favs')
  if (!answerIds.length && !messageIds.length) return null

  return (
    <section className="mb-9" aria-labelledby="notes-pinned-heading">
      <h2 id="notes-pinned-heading" className="mb-3 px-1 text-[11px] font-medium uppercase tracking-[0.14em] text-[#5f6368]">Pinned</h2>
      <ul className="grid list-none grid-cols-[repeat(auto-fill,minmax(240px,1fr))] items-start gap-4 p-0">
        <RootSubsFeed mode="favs" isSubmsges={false} variant="comments" />
        <RootSubsFeed mode="favs" isSubmsges variant="comments" />
      </ul>
    </section>
  )
}

function NotesQuickAnswer() {
  const { state } = useSubsStore()
  const dialog = useQuickReplyTarget()
  if (state.renderProjection.mode !== 'msg' || !selectAccessGrant(state).capabilities.can_reply) return null

  return (
    <section className="mx-auto mb-8 max-w-2xl" aria-label="Create a note">
      {dialog?.elId === 0 ? (
        <QuickReplyComposer dialog={dialog} />
      ) : (
        <input
          type="text"
          readOnly
          value=""
          placeholder="Take a note…"
          aria-label="Create a new answer"
          className="h-12 w-full cursor-text rounded-lg border border-[#e0e0e0] bg-white px-4 text-sm text-[#3c4043] shadow-sm outline-none placeholder:text-[#5f6368] hover:shadow-md focus:border-[#fbbc04]"
          onClick={() => showQuickReplyModal(0, false)}
          onFocus={() => showQuickReplyModal(0, false)}
        />
      )}
    </section>
  )
}

function NotesRootSection({ isSubmsges = false }: { isSubmsges?: boolean }) {
  const { state } = useSubsStore()
  const section = isSubmsges ? 1 : 0
  const frontContract: LegacyContractName = isSubmsges ? 'subs.submsges.front' : 'subs.answers.front'
  const listContract: LegacyContractName = isSubmsges ? 'subs.submsges.rootList' : 'subs.answers.rootList'
  const { currentPage, totalPages } = selectSectionPaging(state, isSubmsges)

  return (
    <div {...legacyHookAttrs(frontContract, 'md-notes-root-section contents')} style={{ display: 'contents' }}>
      <NotesFoldSubsButton section={section} label={isSubmsges ? 'Messages' : 'Answers'} />
      <NotesRefreshSubs section={section} />
      <NotesPagingBar section={section} currentPage={currentPage} totalPages={totalPages} />
      <ul {...legacyHookAttrs(listContract, 'page1 md-notes-root-list !contents')}>
        <RootSubsFeed
          mode="msg"
          activeModes={['msg']}
          isSubmsges={isSubmsges}
          variant="comments"
        />
      </ul>
    </div>
  )
}

function NotesRootFeed() {
  const { state } = useSubsStore()
  const access = selectAccessState(state)
  const { canHaveSubmessages } = selectDeskIdentity(state)
  const canCheckOrMove = selectAccessGrant(state).capabilities.can_check_or_move
  const answersPaging = selectSectionPaging(state, false)
  const messagesPaging = selectSectionPaging(state, true)

  if (state.renderProjection.mode !== 'msg') return null

  return (
    <section
      className="md-notes-normal-feed"
      aria-label="Notes"
      style={{ display: access.showUnlocked ? undefined : 'none' }}
    >
      <div className="mb-3 flex min-h-8 flex-wrap items-center gap-2">
        {canCheckOrMove ? (
          <>
            <NotesCheckedsInfo section={0} />
            {canHaveSubmessages ? <NotesCheckedsInfo section={1} /> : null}
          </>
        ) : (
          <>
            <span aria-hidden="true" style={{ display: 'none' }}><NotesCheckedsInfo section={0} /></span>
            <span aria-hidden="true" style={{ display: 'none' }}><NotesCheckedsInfo section={1} /></span>
          </>
        )}
      </div>
      <div className="grid list-none grid-cols-[repeat(auto-fill,minmax(280px,1fr))] items-start gap-4">
        <NotesRootSection />
        {canHaveSubmessages ? (
          <NotesRootSection isSubmsges />
        ) : (
          <div aria-hidden="true" style={{ display: 'none' }}><NotesRootSection isSubmsges /></div>
        )}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-[#5f6368]">
        <span aria-label="Load more answers"><MoreLink section={0} moreCount={answersPaging.moreCount} /></span>
        {canHaveSubmessages ? (
          <span aria-label="Load more messages"><MoreLink section={1} moreCount={messagesPaging.moreCount} /></span>
        ) : null}
      </div>
    </section>
  )
}

function NotesDialogHost() {
  const { state } = useSubsStore()
  const notesDialog = useNotesDialog()
  const mainUnlocked = selectAccessState(state).showUnlocked
  const isMainOpen = notesDialog.dialog?.kind === 'main'
  const note = notesDialog.dialog?.kind === 'note'
    ? selectSubRenderState(state, notesDialog.dialog.elId)
    : null
  const noteDoc = note ? resolveContentDocument({ bodyHtml: note.html, body: note.text }) : null

  useEffect(() => {
    if (!notesDialog.dialog) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') notesDialog.close()
    }
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('keydown', closeOnEscape)
      document.body.style.overflow = previousOverflow
    }
  }, [notesDialog.dialog, notesDialog.close])

  return (
    <>
      {mainUnlocked ? (
        <div
          className={`fixed inset-0 z-[100010] items-center justify-center bg-slate-950/30 p-4 ${isMainOpen ? 'flex' : 'hidden'}`}
          aria-hidden={!isMainOpen}
          onMouseDown={(event) => {
            if (event.currentTarget === event.target) notesDialog.close()
          }}
        >
          <div role="dialog" aria-modal={isMainOpen || undefined} aria-label="Main note" className="relative max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-xl bg-white p-5 shadow-2xl">
            <button type="button" className="absolute right-3 top-3 z-10 h-9 w-9 rounded-full text-xl text-[#5f6368] hover:bg-[#f1f3f4]" aria-label="Close main note" onClick={notesDialog.close}>×</button>
            <NotesMainMessage />
          </div>
        </div>
      ) : null}
      {note ? (
        <div
          className="fixed inset-0 z-[100011] flex items-center justify-center bg-slate-950/30 p-4"
          onMouseDown={(event) => {
            if (event.currentTarget === event.target) notesDialog.close()
          }}
        >
          <article
            role="dialog"
            aria-modal="true"
            aria-labelledby="notes-preview-title"
            className="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl border border-[#e0e0e0] bg-white p-6 shadow-2xl"
            style={{ backgroundColor: String(note.display?.background_color || '#ffffff') }}
          >
            <button type="button" className="absolute right-3 top-3 h-9 w-9 rounded-full text-xl text-[#5f6368] hover:bg-[#f1f3f4]" aria-label="Close note preview" onClick={notesDialog.close}>×</button>
            <h2 id="notes-preview-title" className="pr-10 text-xl font-medium text-[#202124]">{note.title}</h2>
            <div className="mt-5 text-sm leading-6 text-[#3c4043]">
              {noteDoc ? <ContentView doc={noteDoc} /> : <EmptyContent />}
            </div>
            {note.href ? (
              <footer className="mt-6 border-t border-[#e0e0e0] pt-4">
                <a href={note.href} className="text-sm font-medium text-[#1a73e8] no-underline hover:underline">Open note page</a>
              </footer>
            ) : null}
          </article>
        </div>
      ) : null}
    </>
  )
}

export function NotesPage({
  isEditorOpen = false,
  editorMode,
  settingsOpen,
  onToggleSettings,
  onCloseSettings,
}: {
  isEditorOpen?: boolean
  editorMode?: EditorMode
  settingsOpen: boolean
  onToggleSettings: () => void
  onCloseSettings: () => void
}) {
  const { state } = useSubsStore()
  const editorActive = isEditorOpen || state.renderProjection.mode === 'editor'
  const showUnlocked = selectAccessState(state).showUnlocked

  return (
    <>
      <ShellOverlays />
      <div {...legacyHookAttrs('page.root', 'md-notes-page min-h-screen w-full bg-white text-[#202124]')}>
        <div {...legacyHookAttrs('page.table', 'min-h-screen w-full')}>
          <div {...legacyHookAttrs('page.header', 'h-16 border-b border-[#e0e0e0] bg-white')}>
            <div {...legacyHookAttrs('page.headerCell', 'ml-20 flex h-16 items-center px-5 md:ml-64')}>
              <span className="text-[22px] font-normal text-[#5f6368]">Notes</span>
            </div>
          </div>
          <div className="ml-20 min-h-[calc(100vh-4rem)] md:ml-64">
            <div {...legacyHookAttrs('page.accentCell', 'hidden')} hidden aria-hidden="true" />
            <main {...legacyHookAttrs('page.workspaceCell', 'min-w-0 px-4 py-8 sm:px-8 lg:px-12')}>
              <WorkspaceSection kind="main">
                {!showUnlocked ? (
                  <NotesMainMessage />
                ) : (
                  <>
                    <NotesQuickAnswer />
                    <PinnedGroup />
                    {state.renderProjection.mode === 'msg' ? (
                      <NotesRootFeed />
                    ) : (
                      <>
                        <AnswersPanel />
                        <MessagesPanel />
                      </>
                    )}
                  </>
                )}
              </WorkspaceSection>
              <WorkspaceSection kind="editor">
                <NotesEditorTemplate active={editorActive} editorMode={isEditorOpen ? editorMode : ''} settingsOpen={settingsOpen} onCloseSettings={onCloseSettings} />
              </WorkspaceSection>
              <WorkspaceSection kind="attachments"><NotesMainAttachments /></WorkspaceSection>
              <WorkspaceSection kind="favs"><MenuSubsPanels mode="favs" /></WorkspaceSection>
              <WorkspaceSection kind="trash"><MenuSubsPanels mode="trash" /></WorkspaceSection>
              <WorkspaceSection kind="drafts"><MenuSubsPanels mode="drafts" /></WorkspaceSection>
            </main>
            <div {...legacyHookAttrs('page.rightRail', 'hidden')} hidden aria-hidden="true" />
          </div>
          <div {...legacyHookAttrs('page.footer', 'h-8')} />
        </div>
      </div>
      <NotesMenu isEditorOpen={editorActive} editorMode={editorMode} settingsOpen={settingsOpen} onToggleSettings={onToggleSettings} />
      <NotesDialogHost />
      <EnterPinForm />
      <LoadingOverlay />
      <PinDialog />
    </>
  )
}
